import React, { useEffect, useState } from 'react';
import { Phone, Mail, User, Calendar, CheckCircle, XCircle, Copy, RefreshCw, FileText, Eye, Image, File, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CrmCallTracking from './CrmCallTracking';
import { adminAPI } from '@/utils/api';
import { toast } from 'sonner';

const AdminPartialSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, converted, abandoned
  const [previewFile, setPreviewFile] = useState(null); // { sessionId, fileId, filename, contentType }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null); // base64 data or URL

  useEffect(() => {
    fetchSubmissions();
  }, [filter]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (filter === 'converted') params.converted = true;
      if (filter === 'abandoned') params.converted = false;

      const response = await adminAPI.getPartialSubmissions(params);
      setSubmissions(response.data.submissions || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching partial submissions:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (sessionId, field, currentValue) => {
    try {
      await adminAPI.updatePartialSubmission(sessionId, { [field]: !currentValue });
      setSubmissions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, [field]: !currentValue } : s
      ));
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSaveComment = async (sessionId, text) => {
    try {
      await adminAPI.updatePartialSubmission(sessionId, { admin_comment: text });
      setSubmissions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, admin_comment: text } : s
      ));
      toast.success('Commentaire sauvegardé');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copie`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePreview = async (sessionId, file) => {
    setPreviewFile({ sessionId, ...file });
    setPreviewLoading(true);
    try {
      const response = await adminAPI.getPartialFilePreview(sessionId, file.file_id);
      setPreviewData(response.data);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Erreur lors du chargement de la preview');
      setPreviewFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewData(null);
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('pdf')) return FileText;
    return File;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>
            Contacts a rappeler ({total})
          </h3>
          <Button variant="ghost" size="sm" onClick={fetchSubmissions}>
            <RefreshCw size={16} />
          </Button>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les contacts</SelectItem>
            <SelectItem value="abandoned">Formulaires abandonnes</SelectItem>
            <SelectItem value="converted">Formulaires soumis</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Submissions list */}
      {submissions.length === 0 ? (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <User size={48} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500">Aucun contact disponible</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {submissions.map((sub) => (
            <div
              key={sub.id}
              className={`p-4 rounded-lg border ${
                sub.converted
                  ? 'bg-green-50 border-green-200'
                  : 'bg-orange-50 border-orange-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  {/* Nom */}
                  {sub.nom && (
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-gray-500" />
                      <span className="font-medium">{sub.nom}</span>
                    </div>
                  )}

                  {/* Email */}
                  {sub.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-gray-500" />
                      <a
                        href={`mailto:${sub.email}`}
                        className="text-blue-600 hover:underline"
                      >
                        {sub.email}
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(sub.email, 'Email')}
                      >
                        <Copy size={12} />
                      </Button>
                    </div>
                  )}

                  {/* Telephone */}
                  {sub.telephone && (
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-gray-500" />
                      <a
                        href={`tel:${sub.telephone}`}
                        className="text-blue-600 hover:underline"
                      >
                        {sub.telephone}
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(sub.telephone, 'Telephone')}
                      >
                        <Copy size={12} />
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <a href={`tel:${sub.telephone}`}>
                          <Phone size={12} className="mr-1" />
                          Appeler
                        </a>
                      </Button>
                    </div>
                  )}

                  {/* Date */}
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar size={14} />
                    {formatDate(sub.updated_at)}
                  </div>

                  {/* Files */}
                  {sub.files && sub.files.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <FileText size={14} />
                        {sub.files.length} fichier{sub.files.length > 1 ? 's' : ''} joint{sub.files.length > 1 ? 's' : ''}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {sub.files.map((file) => {
                          const IconComponent = getFileIcon(file.content_type);
                          return (
                            <Button
                              key={file.file_id}
                              variant="outline"
                              size="sm"
                              onClick={() => handlePreview(sub.session_id, file)}
                              className="text-xs"
                            >
                              <IconComponent size={14} className="mr-1" />
                              {file.filename?.length > 20
                                ? file.filename.substring(0, 17) + '...'
                                : file.filename}
                              <Eye size={12} className="ml-1 text-gray-400" />
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Status badge */}
                <div className="flex items-center gap-1">
                  {sub.converted ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle size={12} />
                      Converti
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      <XCircle size={12} />
                      Abandonne
                    </span>
                  )}
                </div>
              </div>

              {/* Admin: Checkboxes + Comment */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <CrmCallTracking
                  answered={sub.answered}
                  wantsLawyer={sub.wants_lawyer}
                  comment={sub.admin_comment}
                  onToggle={(field) => handleToggle(sub.session_id, field, sub[field])}
                  onSaveComment={(text) => handleSaveComment(sub.session_id, text)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden shadow-xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900 truncate flex-1">
                {previewFile.filename}
              </h3>
              <Button variant="ghost" size="sm" onClick={closePreview}>
                <X size={20} />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
              {previewLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="loader"></div>
                </div>
              ) : previewData ? (
                <div className="flex items-center justify-center">
                  {previewData.content_type?.startsWith('image/') ? (
                    <img
                      src={`data:${previewData.content_type};base64,${previewData.content}`}
                      alt={previewFile.filename}
                      className="max-w-full h-auto"
                    />
                  ) : previewData.content_type?.includes('pdf') ? (
                    <iframe
                      src={`data:application/pdf;base64,${previewData.content}`}
                      className="w-full h-[70vh]"
                      title={previewFile.filename}
                    />
                  ) : (
                    <div className="text-center p-8">
                      <File size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">
                        Apercu non disponible pour ce type de fichier
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        {previewData.content_type}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center p-8 text-gray-500">
                  Impossible de charger le fichier
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPartialSubmissions;
